export interface LegalSubsection {
  heading: string;
  items?: string[];
  content?: string;
}

export interface LegalSection {
  id: string;
  heading: string;
  content?: string;
  items?: string[];
  subsections?: LegalSubsection[];
}

export interface LegalCompany {
  name: string;
  address: string;
  email: string;
  phone: string;
  whatsapp: string;
  website: string;
}

export interface PrivacyPolicyData {
  slug: string;
  seo: {
    title: string;
    description: string;
    canonical?: string;
  };
  hero: {
    heading: string;
    lastUpdated: string;
  };
  company?: LegalCompany;
  sections: LegalSection[];
}

export const privacyPolicyData: PrivacyPolicyData = {
  slug: "/privacy-policy",
  seo: {
    title: "Privacy Policy | Seasoft Classes",
    description: "Read the Seasoft Classes Privacy Policy to understand how we collect, use, and protect your personal information.",
    canonical: "https://seasoftclasses.com/privacy-policy",
  },
  hero: {
    heading: "Privacy Policy",
    lastUpdated: "January 1, 2025",
  },
  company: {
    name: "Seasoft Classes",
    address: "15 Huntington Circle, Naperville, IL 60540, USA",
    email: "training@seasoftclasses.com",
    phone: "+1 (708) 766-9765",
    whatsapp: "+1 (209) 210-3187",
    website: "https://seasoftclasses.com",
  },
  sections: [
    {
      id: "section-1",
      heading: "1. Introduction",
      content:
        "At Seasoft Classes we are committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit seasoftclasses.com or use our services. By using our website you agree to the collection and use of information as described here. This policy applies to our website, online training programs, email communications, career counseling services, and WhatsApp and phone channels.",
    },
    {
      id: "section-2",
      heading: "2. Information We Collect",
      subsections: [
        {
          heading: "2.1 Information You Provide",
          items: [
            "Personal details from contact and enrollment forms: full name, email, phone, country, occupation",
            "Course enrollment details: preferences, educational background, payment information",
            "Communication records from email, phone, WhatsApp, and contact form interactions",
            "Career information for placement support: resume, work history, qualifications, skills",
          ],
        },
        {
          heading: "2.2 Information Collected Automatically",
          items: [
            "Usage data: IP address, browser type, OS, pages visited, time on page, links clicked",
            "Cookies and tracking technologies to enhance browsing and analyze traffic",
            "Google Analytics 4 data: session duration, page views, traffic sources, demographics",
          ],
        },
      ],
    },
    {
      id: "section-3",
      heading: "3. How We Use Your Information",
      items: [
        "Process course enrollment and deliver training materials and live sessions",
        "Send enrollment confirmations, course updates, and respond to inquiries",
        "Send relevant job opening alerts and new course information",
        "Analyze usage patterns to improve website and course quality",
        "Comply with legal and regulatory requirements",
        "Send promotional communications with your consent — opt out anytime",
      ],
    },
    {
      id: "section-4",
      heading: "4. How We Share Your Information",
      content:
        "We do not sell, trade, or rent your personal information. We share information only with: trusted service providers (payment processors, email services, CRM tools) under confidentiality agreements; employer partners for placement services with your explicit consent; analytics providers using anonymized data; and when required by law.",
    },
    {
      id: "section-5",
      heading: "5. Data Retention",
      items: [
        "Enrollment and course records: 7 years",
        "Contact and inquiry records: 3 years from last contact",
        "Marketing preferences: duration of relationship plus 3 years",
        "Website analytics data: 26 months",
      ],
    },
    {
      id: "section-6",
      heading: "6. Your Rights and Choices",
      items: [
        "Right to Access: request a copy of your personal information",
        "Right to Correction: request correction of inaccurate data",
        "Right to Deletion: request deletion subject to legal obligations",
        "Right to Opt Out of Marketing: unsubscribe via email link or contact us",
        "Right to Data Portability: receive data in a structured format",
        "California Residents (CCPA): right to know, delete, and opt out of data sale — we do not sell data",
      ],
    },
    {
      id: "section-7",
      heading: "7. Cookie Policy",
      items: [
        "Strictly Necessary Cookies: essential for website function, cannot be disabled",
        "Performance Cookies: Google Analytics for anonymized usage data",
        "Functionality Cookies: remember your preferences and settings",
        "Marketing Cookies: managed via Google Tag Manager for relevant advertising",
      ],
    },
    {
      id: "section-8",
      heading: "8. Third-Party Links",
      content:
        "Our website may link to third-party sites including job portals and social media. We are not responsible for their privacy practices. Please read the privacy policy of any third-party site you visit.",
    },
    {
      id: "section-9",
      heading: "9. Children's Privacy",
      content:
        "Our services are for individuals 18 years or older. We do not knowingly collect data from children under 13. If you believe a child has provided us information, contact training@seasoftclasses.com immediately.",
    },
    {
      id: "section-10",
      heading: "10. Security",
      items: [
        "SSL/TLS encryption for all data transmission",
        "Secure storage with access controls on a need-to-know basis",
        "Regular security assessments and updates",
        "PCI-compliant third-party payment processing",
      ],
    },
    {
      id: "section-11",
      heading: "11. International Transfers",
      content:
        "Seasoft Classes is based in the United States and serves students globally. Data may be transferred to and processed in the US. By using our services you consent to this transfer.",
    },
    {
      id: "section-12",
      heading: "12. Changes to This Policy",
      content:
        "We may update this policy periodically. We will update the Last Updated date when changes are made. Continued use of our services after changes constitutes acceptance.",
    },
    {
      id: "section-13",
      heading: "13. Contact Us",
      content:
        "For privacy questions contact us at training@seasoftclasses.com or 15 Huntington Circle, Naperville, IL 60540, USA. Phone: +1 (708) 766-9765.",
    },
  ],
};

